import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { withTranslation } from 'react-i18next';
import { getNameFile } from 'core/utils/utils';
import { Button, FormField, Icon } from 'ui';
import AttachEntry from '../components/AttachEntry';
import CharacterLimitTextArea from '../components/general/CharacterLimitTextArea/CharacterLimitTextArea';

function getFileName(file) {
  if (file.url) return getNameFile(file.url);
  const name = getNameFile(file.encoded_url ?? '');
  try {
    return decodeURIComponent(name);
  } catch {
    return name;
  }
}

const FormEdit = ({
  t,
  isComment,
  commentGuid,
  onEdit,
  onClose,
  comment,
  files,
  attachedFiles,
  removeRecentlyAttached,
  onAttachComment,
  onRemoveAttachment,
}) => {
  const [value, setValue] = useState(comment || '');
  const [sendEnabled, setSendEnabled] = useState(true);
  const [currentFiles, setCurrentFiles] = useState(files || []);

  const textAreaRef = React.useRef(null);
  const attachmentsRef = React.useRef(null);
  const pendingFocusIndex = React.useRef(null);

  const MAX_CHARACTERS = 60000;
  const isAtCharacterLimit = value.length >= MAX_CHARACTERS;
  const limitMessage = isAtCharacterLimit ? t('common:characterLimitReached', { quantity: MAX_CHARACTERS }) : '';

  function onChange(e) {
    let formatted = e.currentTarget.value.substring(0, MAX_CHARACTERS);
    setValue(formatted);
    setSendEnabled(formatted.length > 1);
  }

  function editComment() {
    onEdit(commentGuid, value);
  }

  // El botón pulsado desaparece: el foco pasa al siguiente «quitar» o, si no queda, al campo
  React.useEffect(() => {
    if (pendingFocusIndex.current === null) return;
    const removeButtons = attachmentsRef.current?.querySelectorAll('.post-card__attachment-remove, .upload-attach__entry-remove') ?? [];
    const target = removeButtons[Math.min(pendingFocusIndex.current, removeButtons.length - 1)] ?? textAreaRef.current;
    pendingFocusIndex.current = null;
    target?.focus();
  }, [currentFiles, attachedFiles]);

  function removeAttachment(fileGuid, index) {
    pendingFocusIndex.current = index;
    onRemoveAttachment(fileGuid, commentGuid);
    setCurrentFiles(currentFiles.filter((file) => file.guid !== fileGuid));
  }

  function removeRecentAttachment(fileGuid, index) {
    pendingFocusIndex.current = currentFiles.length + index;
    removeRecentlyAttached(fileGuid);
  }

  return (
    <div className="form-delete-collection" ref={attachmentsRef}>
      <FormField isLabelHidden label={t('actions:Edit')}>
        <CharacterLimitTextArea ref={textAreaRef} limitMessage={limitMessage} onChange={onChange} placeholder="" value={value} />
      </FormField>
      <br />
      <div className="form-edit-message__attachments">
        {currentFiles.map((file, k) => {
          return (
            <div className="post-card__attachment" key={file.guid ?? k}>
              <div className="post-card__attachment-link">
                <div className="post-card__attachment-icon">
                  {/*<IconDLO type="attach" />*/}
                  <Icon name="attach" size="xl" />
                </div>

                <a
                  href={file.encoded_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={t('buttons:Open file in new tab')}
                >
                  {file.encoded_url}
                </a>
              </div>
              <Button
                ariaLabel={t('actions:Delete file', { name: getFileName(file) })}
                className="post-card__attachment-remove"
                iconSize="xl"
                isIconOnly
                leftIcon="close"
                onClick={() => removeAttachment(file.guid, k)}
                variant="plain"
              />
            </div>
          );
        })}
      </div>
      <div className="upload-attach__uploader-list">
        {attachedFiles.map((content, k) => {
          return <AttachEntry key={content.guid} guid={content.guid} name={content.name} size={content.size} onRemove={() => removeRecentAttachment(content.guid, k)} />;
        })}
      </div>
      <div className="form-delete-collection__actions">
        <div className="form-edit-message__buttons">
          <div className="form-delete-message__buttons-left">
            {!isComment && onAttachComment && (
              <Button
                ariaLabel={t('my-courses:attachContent')}
                className="form-delete-message__attach"
                iconSize="xl"
                isIconOnly
                leftIcon="attach"
                onClick={onAttachComment}
                variant="plain"
              />
            )}
          </div>
          <div className="form-delete-message__buttons-right">
            <div style={{ display: 'inline-block', float: 'right', width: '150px' }}>
              <Button variant="secondary-ghost" onClick={onClose} text={t('Cancel')} isFullWidth />
            </div>
            <div style={{ display: 'inline-block', float: 'right', width: '150px' }}>
              <Button isDisabled={!sendEnabled} variant="primary" onClick={editComment} text={t('actions:Save')} isFullWidth />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

FormEdit.propTypes = {
  attachedFiles: PropTypes.arrayOf(PropTypes.object),
  comment: PropTypes.string,
  commentGuid: PropTypes.string,
  files: PropTypes.arrayOf(PropTypes.object),
  isComment: PropTypes.bool,
  onAttachComment: PropTypes.func,
  onClose: PropTypes.func,
  onEdit: PropTypes.func,
  onRemoveAttachment: PropTypes.func,
  removeRecentlyAttached: PropTypes.func,
  t: PropTypes.func,
};

FormEdit.defaultProps = {
  files: [],
  attachedFiles: [],
};

export default withTranslation(['actions', 'buttons'])(FormEdit);
